import React from "react"
import {
	Flex,
	Text,
	Button,
	FilterCheckbox,
	INNER_MAX_WIDTH,
	colors,
} from "@capioweb/ui-components/src"
import { LocalesContext } from "../../../context"
import AnimateHeight from "react-animate-height"
import { UnitFilterContext } from "../context/UnitFilterContext"
import { UnitFilterProps, UnitFilterValue } from "../CapioNexusUnitFilterBlock"
import { sendOpenFilterDataLayerPush } from "../../../utils"
import styled from 'styled-components'
import { breakpoints } from '@capioweb/ui-components'
import { getFilterQueryParams } from "../../../utils/filterHistory"

interface FilterSectionProps {
	heading?: string
	filterValues: UnitFilterValue[]
	lastChild?: boolean
	onChange: any
	filterType?: 'careType' | 'fieldOfCare'
}

const FilterSection: React.FC<FilterSectionProps> = ({
	heading,
	filterValues,
	lastChild,
	onChange,
	// filterType
}) => {
	const {
		activeRegionIds,
		activeCareTypeIds,
		activeDiagnoseFirstLevelIds,
		activeDiagnoseSecondLevelIds,
		// checkAll,
	} = React.useContext(UnitFilterContext)

	return (
		<Flex
			w={1}
			flexDirection="column"
			pb={1.5}
			mb={{ _: 0, m: lastChild ? 2 : 3 }}
			borderBottom={lastChild ? false : { _: false, m: true }}
		>
			{heading && (
				<Text
					fontSize={1.75}
					lineHeight={2.5}
					fontWeight="bold"
					letterSpacing="1px"
					textTransform="uppercase"
					mb={1.5}
				>
					{heading}
				</Text>
			)}
			{filterValues?.length > 0 && (
				<Flex w={1} flexWrap="wrap">
					{filterValues.map((filter) => {

						return (
							<Flex pb={1.5} pr={1.5} key={filter.id}>
								<FilterCheckbox
									checked={
										activeRegionIds?.includes(filter.id) ||
										activeCareTypeIds?.includes(filter.id) ||
										activeDiagnoseFirstLevelIds?.includes(filter.id) ||
										activeDiagnoseSecondLevelIds?.includes(filter.id)
									}
									label={filter.name}
									value={filter.id.toString()}
									onChange={() => onChange(filter.id)
									}
									withoutBorder={true}
								/>
							</Flex>
						)
					})}
				</Flex>
			)}
		</Flex>
	)
}

const UnitFilter: React.FC<UnitFilterProps> = ({
	regionFilterValues,
	careTypesFilterValues,
	diagnoseFirstLevelFilterValues,
	diagnoseSecondLevelFilterValues,
	disableCareTypeFilter,
}) => {

	const { translate, translationKeys } = React.useContext(LocalesContext)
	// Toggle for filter
	const [filterOpen, setFilterOpen] = React.useState(false)
	const [filterModalOpen, setFilterModalOpen] = React.useState(false)

	const modalRef = React.useRef<HTMLDivElement>(null)
	const buttonRef = React.useRef<HTMLDivElement>(null)

	const {
		updateActiveRegionIds,
		updateActiveCareTypeIds,
		updateActiveDiagnoseFirstLevelIds,
		updateActiveDiagnoseSecondLevelIds,
		activeDiagnoseFirstLevelFilterValues,
		activeDiagnoseSecondLevelFilterValues,
		resetFilters,
		totalHits
	} = React.useContext(UnitFilterContext)

	const hasRegionOrCareTypePreset = getFilterQueryParams("regions")?.length > 0 || getFilterQueryParams("caretypes")?.length > 0
	const hasCareareasPreset = getFilterQueryParams("careareas")?.length > 0

	const diagnoseLevelOneFilterValues = (activeDiagnoseFirstLevelFilterValues ?? [])?.length <= 0 && hasRegionOrCareTypePreset ? diagnoseFirstLevelFilterValues : activeDiagnoseFirstLevelFilterValues
	const diagnoseLevelTwoFilterValues = (activeDiagnoseSecondLevelFilterValues ?? [])?.length <= 0 && hasCareareasPreset ? diagnoseSecondLevelFilterValues : activeDiagnoseSecondLevelFilterValues

	const ButtonWrapperStyled = styled.div`	
	width: 100%;
	box-shadow: 0px -8px 16px rgba(0, 0, 0, 0.05);
	border-top: 2px solid ${colors.blue33};
	`
	// FocusTrap for modal
	const [firstFocableElement, setFirstFocableElement] =
		React.useState<HTMLButtonElement | HTMLAnchorElement | HTMLInputElement>()

	React.useEffect(() => {
		if (modalRef.current) {
			const focableElements = Array.from(
				modalRef.current?.querySelectorAll(
					'button:not([tabindex="-1"]), a:not([tabindex="-1"]), input:not([tabindex="-1"])'
				),
			)
			setFirstFocableElement(
				focableElements[0] as HTMLButtonElement | HTMLAnchorElement | HTMLInputElement
			)
		}
		firstFocableElement?.focus()
	}, [modalRef.current, filterModalOpen])

	React.useEffect(() => {
		if (document) {
			document.addEventListener('keydown', trapFocus)
			return () => {
				document.removeEventListener("keydown", trapFocus)
			}
		}
	}, [modalRef.current])

	const trapFocus = (e: KeyboardEvent) => {

		const lastChild = document.getElementById('button-last-child')

		if (e.key === 'Escape') {
			setFilterModalOpen(false)
			// returnFocusOnClose()
		}

		if (e.key !== 'Tab') return

		if (!firstFocableElement) return

		if (e.shiftKey) {
			// Shift + Tab
			if (document.activeElement === firstFocableElement) {
				e.preventDefault()
				lastChild.focus()
			}
		} else {
			// Tab

			if (document.activeElement === lastChild) {
				e.preventDefault()
				firstFocableElement.focus()
			}
		}
	}
	const handleScroll = () => {
		if (buttonRef.current)
			scrollTo(0, buttonRef.current.offsetTop)
	}

	React.useEffect(() => {
		//Toggle overflow hidden when filter modal is open/closed,
		//to stop scrolling in background
		if (document) {
			if (filterModalOpen) document.body.style.overflow = 'hidden'
			else document.body.style.overflow = 'auto'
		}
	}, [filterModalOpen])

	React.useEffect(() => {
		//Removes overflow hidden when window width is greater than 768px
		const handleResize = () => {
			if (window.innerWidth > breakpoints.m) {
				document.body.style.overflow = 'auto'
			}
		}
		window.addEventListener('resize', handleResize)
		return () => {
			window.removeEventListener('resize', handleResize)
		}
	}, [window.innerWidth])

	return (
		<>
			<Flex w={1} py={3} justifyContent="center" backgroundColor="blue11" display={{ _: 'none', m: 'flex' }}>
				<Button
					variant="ghost-navy"
					icon="filter"
					textColor="navy"
					onClick={() => {
						setFilterOpen(!filterOpen)
						sendOpenFilterDataLayerPush(filterOpen ? "close" : "open")
					}}
				>
					{`${filterOpen
						? translate(translationKeys.close)
						: translate(translationKeys.open)
						} ${translate(translationKeys.filter)}`}
				</Button>
			</Flex>
			<Flex ref={buttonRef} w={1} justifyContent="center" display={{ _: 'flex', m: 'none' }}>
				<Button

					variant="light-blue"
					icon="filter"
					textColor="navy"
					onClick={() => {
						setFilterModalOpen(!filterModalOpen)
						sendOpenFilterDataLayerPush(filterModalOpen ? "close" : "open")
					}}
				>
					{`${filterModalOpen
						? translate(translationKeys.close)
						: translate(translationKeys.open)
						} ${translate(translationKeys.filter)}`}
				</Button>
			</Flex>
			<AnimateHeight height={filterOpen ? "auto" : 0}>
				<Flex w={1} px={3} backgroundColor="blue11">
					<Flex w={1} flexDirection="column" maxw={INNER_MAX_WIDTH} m="0 auto">
						{/* Filter layout for screens >= l */}
						<Flex w={1} display={{ _: "none", m: "block" }}>
							{/* Region filters */}
							{regionFilterValues?.length > 0 && (
								<FilterSection
									heading={translate(translationKeys.regions)}
									filterValues={regionFilterValues}
									lastChild={disableCareTypeFilter?.value === true}
									onChange={updateActiveRegionIds}
								/>
							)}

							{/* Care type filters */}
							{disableCareTypeFilter?.value !== true &&
								careTypesFilterValues?.length > 0 && (
									<FilterSection
										heading={translate(translationKeys.typeOfCare)}
										filterValues={careTypesFilterValues}
										onChange={updateActiveCareTypeIds}
										lastChild={diagnoseLevelOneFilterValues?.length <= 0}
										filterType='careType'
									/>
								)}

							{/* Diagnose First Level filters */}
							{diagnoseLevelOneFilterValues?.length > 0 && (
								<FilterSection
									heading={translate(translationKeys.fieldOfCare)}
									filterValues={diagnoseLevelOneFilterValues}
									onChange={updateActiveDiagnoseFirstLevelIds}
									lastChild={diagnoseLevelTwoFilterValues?.length <= 0}
									filterType='fieldOfCare'
								/>
							)}
							{/* Diagnose Second Level filters */}
							{diagnoseLevelTwoFilterValues?.length > 0 && (
								<FilterSection
									heading={translate(translationKeys.diagnoses)}
									filterValues={diagnoseLevelTwoFilterValues}
									onChange={updateActiveDiagnoseSecondLevelIds}
									lastChild
								/>
							)}
						</Flex>
					</Flex>
				</Flex>
			</AnimateHeight>

			{/* Filter layout for screens < l */}
			{filterModalOpen &&
				<Flex
					aria-labelledby='unit-filter-modal-title'
					role='dialog'
					aria-modal
					position='fixed'
					p={{ m: 3 }}
					flexDirection='column'
					zIndex={50}
					top={0}
					left={0}
					overflow='scroll'
					w='100vw'
					h='100vh'
					justifyContent='space-between'
					backgroundColor='blue11'
					display={{ _: 'flex', m: 'none' }}>
					<Flex w={1} flexDirection="column" maxw={INNER_MAX_WIDTH} m="0 auto" p={2}>
						<Text id='unit-filter-modal-title' as='h2' variant='headline-2' mb={3}>
							{translate(translationKeys.toFilter)}
						</Text>
						{/* Filter layout for screens >= l */}
						<Flex w={1} display='block' ref={modalRef}>
							{/* Region filters */}
							{regionFilterValues?.length > 0 && (
								<FilterSection
									heading={translate(translationKeys.regions)}
									filterValues={regionFilterValues}
									lastChild={disableCareTypeFilter?.value === true}
									onChange={updateActiveRegionIds}
								/>
							)}

							{/* Care type filters */}
							{disableCareTypeFilter?.value !== true &&
								careTypesFilterValues?.length > 0 && (
									<FilterSection
										heading={translate(translationKeys.typeOfCare)}
										filterValues={careTypesFilterValues}
										onChange={updateActiveCareTypeIds}
										lastChild={activeDiagnoseFirstLevelFilterValues?.length <= 0}
										filterType='careType'
									/>
								)}

							{/* Diagnose First Level filters */}
							{activeDiagnoseFirstLevelFilterValues?.length > 0 && (
								<FilterSection
									heading={translate(translationKeys.fieldOfCare)}
									filterValues={activeDiagnoseFirstLevelFilterValues}
									onChange={updateActiveDiagnoseFirstLevelIds}
									lastChild={activeDiagnoseSecondLevelFilterValues?.length <= 0}
									filterType='fieldOfCare'
								/>
							)}
							{/* Diagnose Second Level filters */}
							{activeDiagnoseSecondLevelFilterValues?.length > 0 && (
								<FilterSection
									heading={translate(translationKeys.diagnoses)}
									filterValues={activeDiagnoseSecondLevelFilterValues}
									onChange={updateActiveDiagnoseSecondLevelIds}
									lastChild
								/>
							)}
						</Flex>
					</Flex>


					<ButtonWrapperStyled>
						<Flex p={3} columnGap={3} justifyContent='center' w={1} backgroundColor='blue11'>
							<Button variant='ghost-navy' onClick={() => {
								setFilterModalOpen(!filterModalOpen)
								resetFilters()
								handleScroll()
							}}>{translate(translationKeys.clear)}</Button>
							<Button id='button-last-child' variant='navy' onClick={() => {
								setFilterModalOpen(!filterModalOpen)
								handleScroll()
							}}>
								{translate(translationKeys.unitFilterResultCountMobile).replace(
									/%s/g,
									totalHits
								)}
							</Button>
						</Flex>
					</ButtonWrapperStyled>
				</Flex>
			}
		</>
	)
}

export { UnitFilter }